import { createContext, FC, PropsWithChildren, useContext } from 'react'
import { useSelector } from 'react-redux'
import { toNumber } from 'lodash'
import { select } from '@/store'
import useGetSuiteInfo from '@/hooks/get-suite-info'
import useGetInsights from '@/hooks/use-get-insights'
import useGetListingDetails from '@/hooks/use-get-listing-details'
import { useListingTimelines } from '@/hooks/use-listing-timelines'
import { checkRestrictedListing } from '@/utils/check-restricted-listing'
import { getEditMlsListingLink } from '@/utils/getEditMlsListingLink'
import { useListingViews } from '@/components/useListingViews'
import { useListingImpressions } from '@/components/useListingImpressions'
import { useSponsorshipData } from './use-sponsorship-data'

interface ListingImage {
  aws_s3_key: string
  id: string
  location: string
}

interface ListingData {
  loading: boolean
  listingId: string
  myPhoneNumber?: string
  coverPhoto?: ListingImage
  unitType?: string
  bathrooms?: number
  bedrooms?: number
  price?: number
  priceFrequency?: string
  groupListing?: boolean
  groupMinimumPrice?: number
  listingAddress?: string
  isImported?: boolean
  sourceAbbreviation?: string
  sourceName?: string
  applicationCount?: number
  unreadCount?: number
  listingSponsored?: boolean
  currentSponsorship?: any
  listingVerified?: boolean
  listingVerificationRequested?: boolean
  listingVerificationByMail?: boolean
  listingPending?: boolean
  listingHidden?: boolean
  viewCount?: number
  impressionCount?: number
  chatCount?: number
  tags: Array<string>
  externalInfo?: any
  link?: string
  createdAt?: string
  lastUpdatedAt?: string
  averagePrice?: number
  priceDifference?: number
}

const ListingContext = createContext<ListingData>({
  listingId: '',
  loading: false,
  tags: []
})

interface Props extends PropsWithChildren {
  listingId: string
}

export const ListingDataProvider: FC<Props> = ({ listingId, children }) => {
  const profile: any = useSelector(select.profile.profile)

  const suiteInfo = useGetSuiteInfo(listingId) as any
  const listingTimelinesHook = useListingTimelines({ listingId })
  const listingDetailsHook = useGetListingDetails(listingId)
  const sponsorships = useSponsorshipData({ listingId })
  const listingViewsHook = useListingViews({ listingId, period: '7d' })
  const listingImpressionsHook = useListingImpressions({ listingId, period: '7d' })
  const listingInsights = useGetInsights(listingId)

  const averagePrice = listingInsights.priceInfo.average
  const priceDifference = listingInsights.priceInfo.difference

  const timelines = Object.values(listingTimelinesHook.data)
  const applications = timelines.filter(x => !!x.applications?.id)
  const { link } = getEditMlsListingLink(suiteInfo?.external_info)

  const verificationTypes = listingDetailsHook.data?.listings?.verified_request_types ?? []
  const listingVerificationByMail = verificationTypes.includes('mail')

  return (
    <ListingContext.Provider
      value={{
        listingId,
        loading:
          !suiteInfo ||
          listingTimelinesHook.isLoading ||
          listingDetailsHook.isLoading ||
          listingViewsHook.isLoading ||
          listingImpressionsHook.isLoading ||
          sponsorships.isLoading,

        // Current user details
        myPhoneNumber: profile?.phone_number,

        // Listing details
        coverPhoto: {
          id: suiteInfo?.cover_photo_id,
          location: suiteInfo?.cover_photo,
          aws_s3_key: suiteInfo?.cover_photo_aws_s3_key
        },
        listingAddress:
          (suiteInfo?.unit_number ? `${suiteInfo?.unit_number} - ` : '') +
          (suiteInfo?.full_street_name || ''),
        unitType: suiteInfo?.unit_type_scope_txt_id,
        bedrooms: toNumber(suiteInfo?.bedrooms),
        bathrooms: toNumber(suiteInfo?.bathrooms),
        price: suiteInfo?.price,
        priceFrequency: suiteInfo?.price_frequency,
        groupListing: suiteInfo?.gr_unit === '1',
        groupMinimumPrice: toNumber(suiteInfo?.gr_min_price),

        // Listing status
        listingSponsored: sponsorships.hasBeenSponsored,
        currentSponsorship: sponsorships.currentSponsorship,
        isImported: checkRestrictedListing(suiteInfo?.tags),
        listingVerified: suiteInfo?.verified_state_machine === 'approved',
        listingVerificationRequested: suiteInfo?.verified_state_machine === 'requested',
        listingVerificationByMail,
        listingPending: suiteInfo?.state_machine === 'pending',
        listingHidden: suiteInfo?.is_hidden === '1',

        // Analytics
        viewCount: toNumber(listingViewsHook.data.reduce((acc, x) => acc + x.count, 0)),
        impressionCount: toNumber(listingImpressionsHook.data.reduce((acc, x) => acc + x.count, 0)),
        chatCount: timelines.length,
        unreadCount: timelines.reduce((count, { is_read }) => count + (is_read === '0' ? 1 : 0), 0),
        applicationCount: applications?.length,

        tags: suiteInfo?.external_info?.source_info_abbreviation ?? [],
        externalInfo: suiteInfo?.external_info,
        sourceAbbreviation: suiteInfo?.external_info?.source_info_abbreviation,
        sourceName: suiteInfo?.external_info?.source_info_name,
        link,
        createdAt: suiteInfo?.created_at,
        lastUpdatedAt: '',

        averagePrice: typeof averagePrice === 'number' ? averagePrice : undefined,
        priceDifference:
          (listingInsights.priceInfo.aboveAverage ? 1 : -1) *
          Math.round(typeof priceDifference === 'number' ? priceDifference : 0)
      }}
    >
      {children}
    </ListingContext.Provider>
  )
}

export const useListingData = () => useContext(ListingContext)
